import styled from '@emotion/styled';
import {useOnboardingStep} from '../../hooks/useOnboardingStep';
import {xrStore} from '../../store/xrStore';
import {EyeToTrain, TrainingMode} from '../../types/exercises';
import {VRPreviewScene} from '../app/VRPreviewScene';

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(10px);
`;

const ModalContent = styled.div`
  background: #1e1e1e;
  border-radius: 24px;
  width: 90vw;
  max-width: 1200px;
  height: 80vh;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4), 0 8px 16px rgba(0, 0, 0, 0.3);
`;

const PreviewPanel = styled.div`
  flex: 1;
  background: #000000;
  border-radius: 24px 24px 0 0;
  overflow: hidden;
  position: relative;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 2rem 2.5rem;
  background: #1e1e1e;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const Button = styled.button<{$variant?: 'primary' | 'secondary'}>`
  flex: 1;
  padding: 1rem 2rem;
  border-radius: 999px;
  border: ${props => (props.$variant === 'primary' ? '2px solid #00ff88' : 'none')};
  background: ${props => (props.$variant === 'primary' ? '#1a2530' : 'rgba(255, 255, 255, 0.1)')};
  color: ${props => (props.$variant === 'primary' ? '#f5f5f5' : '#ffffff')};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease-out;
  letter-spacing: 0.01em;

  &:hover {
    background: ${props => (props.$variant === 'primary' ? '#1a2530' : 'rgba(255, 255, 255, 0.15)')};
    transform: translateY(-1px);
    box-shadow: ${props =>
      props.$variant === 'primary'
        ? '0 0 20px rgba(0, 255, 136, 0.4), 0 0 40px rgba(0, 255, 136, 0.2)'
        : '0 4px 12px rgba(0, 0, 0, 0.3)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

interface VRPreviewModalProps {
  isInitialized: boolean;
  onClose: () => void;
  eyeToTrain: EyeToTrain;
  currentExercise: TrainingMode;
  children: React.ReactNode;
  settingsKey?: string;
}

export const VRPreviewModal: React.FC<VRPreviewModalProps> = ({
  onClose,
  eyeToTrain,
  children,
  settingsKey,
}) => {
  const {markAsCompleted} = useOnboardingStep('start_session');

  const handleEnterVR = async () => {
    try {
      // Get the current session
      const session = xrStore.getState().session;

      // If we already have a session, end it first
      if (session) {
        await session.end();
      }

      // Start a new VR session
      await xrStore.enterVR();
      markAsCompleted('start_session');
    } catch (error) {
      // Error entering VR
    }
  };

  return (
    <ModalContainer>
      <ModalContent>
        <PreviewPanel>
          <VRPreviewScene key={settingsKey} eye={eyeToTrain === 'both' ? 'left' : eyeToTrain}>
            {children}
          </VRPreviewScene>
        </PreviewPanel>
        <ButtonContainer>
          <Button onClick={onClose}>Cancel</Button>
          <Button $variant="primary" onClick={handleEnterVR}>
            Enter VR
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalContainer>
  );
};
